import React from "react"

import { PartnersInvitationCreateBody } from "@boundless/bl-api-client/partners/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { SendIcon } from "lucide-react"
import { useForm } from "react-hook-form"

import { FormInput } from "@/components/form-elements/FormInput"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import {
  useUsers,
  type InviteSchema,
} from "@/components/users/providers/UserProvider"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

export function InviteUserDialog() {
  const [open, setOpen] = React.useState(false)

  const { invite, isInvitePending } = useUsers()

  const form = useForm<InviteSchema>({
    resolver: zodResolver(PartnersInvitationCreateBody),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  })

  const handleInvite = (data: InviteSchema) => {
    invite({
      data,
      onSuccess: () => {
        form.reset()
        setOpen(false)
      },
      onError: (error) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Invite Member</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a Member</DialogTitle>
          <DialogDescription>
            They will receive an email to complete their account.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleInvite)}
          >
            <FormInput name="firstName" placeholder="First Name" />
            <FormInput name="lastName" placeholder="Last Name" />
            <FormInput name="email" placeholder="Email" type="email" />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              {isInvitePending ? (
                <LoadingButton />
              ) : (
                <Button type="submit">
                  <SendIcon />
                  Send
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
